import React from 'react';
import PropTypes from 'prop-types';
import Badge from '../../../../components/ui/Badge';
import useEmployeeFinanceText from '../employeeFinanceText';
import { YES_NO_KEYS, isYes } from '../employeeFinanceColumns';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';
const WORK_TYPE_KEYS = { 'Full Time': 'fullTime', 'Part Time': 'partTime' };

export const isBlank = (value) => value === null || value === undefined || value === ''
  || (typeof value === 'number' && Number.isNaN(value))
  || (typeof value === 'string' && ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(value.toLowerCase()));

export const initialsOf = (emp) => `${(emp.eName || '').charAt(0)}${(emp.eSurname || '').charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';

export const STATUS_GRADIENT = {
  active: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
  inactive: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
};

/** Aktif / İnaktif (Active: 1 / other); "-" when the field is missing. */
export const ActiveBadge = ({ value }) => {
  const { label } = useEmployeeFinanceText();
  if (value === undefined || value === null || Number.isNaN(Number(value))) return <span className={MUTED}>-</span>;
  return value === 1
    ? <Badge tone="success">{label('status.active')}</Badge>
    : <Badge tone="neutral">{label('status.inactive')}</Badge>;
};

ActiveBadge.propTypes = { value: PropTypes.any };

/** Display text of a field (Evet / Hayır, Türkiye for 173, dates, work type). */
export const useFieldText = () => {
  const { label, formatDate } = useEmployeeFinanceText();
  return (emp, key) => {
    const value = emp[key];
    if (YES_NO_KEYS.includes(key)) return isBlank(value) ? label('yesNo.no') : (isYes(value) ? label('yesNo.yes') : label('yesNo.no'));
    if (isBlank(value)) return '';
    if (key === 'nationality') return value === 173 || value === '173' ? label('turkey') : String(value);
    if (key === 'workType') return WORK_TYPE_KEYS[value] ? label(`workTypes.${WORK_TYPE_KEYS[value]}`) : String(value);
    if (key === 'birthday' || key === 'startDate') return formatDate(value);
    return String(value);
  };
};

/** Content of one table cell. */
const EmployeeFinanceCell = ({ columnKey, employee }) => {
  const fieldText = useFieldText();
  if (columnKey === 'Active') return <ActiveBadge value={employee.Active} />;
  if (columnKey === 'eName') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${employee.Active === 1 ? STATUS_GRADIENT.active : STATUS_GRADIENT.inactive}`}>
          {initialsOf(employee)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{employee.eName || '-'}</span>
      </span>
    );
  }
  const text = fieldText(employee, columnKey);
  if (!text) return <span className={MUTED}>-</span>;
  return <span className={`tw-block tw-truncate ${columnKey === 'eSurname' ? 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' : ''}`}>{text}</span>;
};

EmployeeFinanceCell.propTypes = { columnKey: PropTypes.string.isRequired, employee: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const EmployeeFinanceCardRow = ({ employee }) => (
  <>
    <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${employee.Active === 1 ? STATUS_GRADIENT.active : STATUS_GRADIENT.inactive}`}>
      {initialsOf(employee)}
    </span>
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${employee.eName || ''} ${employee.eSurname || ''}`.trim() || '-'}</span>
        <ActiveBadge value={employee.Active} />
      </span>
      <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
        {[employee.departman, employee.phone].filter((value) => !isBlank(value)).join(' · ') || '-'}
      </span>
    </span>
  </>
);

EmployeeFinanceCardRow.propTypes = { employee: PropTypes.object.isRequired };

export default EmployeeFinanceCell;
